import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typography, Button, Spin, Alert, Input, Tag, Modal, message } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';

import { professorAvaliacoesService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import { STATUS_ALUNO_AVALIACAO_INFO } from './AlunoAvaliacaoProfessorCard';
import '../../styles/PortalProfessor.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const MENSAGEM_SEM_VINCULO =
  'Seu usuário ainda não está vinculado a um professor/equipe. Procure a administração.';
const MENSAGEM_NAO_ENCONTRADA = 'Esta avaliação não está disponível.';

// 4 opções fixas do contrato real (avaliacao_aluno_service.NIVEIS_VALIDOS,
// espelhado em RespostaAvaliacaoRequest) -- escala institucional (Fase 11):
// o valor enviado ao backend é sempre um destes 4, só o rótulo é amigável.
const NIVEL_OPCOES = [
  { value: 'EM_DESENVOLVIMENTO', label: 'Em desenvolvimento', color: '#d48806' },
  { value: 'ADEQUADO', label: 'Adequado', color: '#1677ff' },
  { value: 'BOM', label: 'Bom', color: '#389e0d' },
  { value: 'AVANCA_ALEM_DO_ESPERADO', label: 'Avança além do esperado', color: '#722ed1' },
];
const NIVEL_LABEL = Object.fromEntries(NIVEL_OPCOES.map((o) => [o.value, o.label]));

// Ordem institucional fixa dos grupos (Fase 11) -- gerais primeiro, na
// mesma ordem do relatório semestral, técnico por último. Um grupo sem
// nenhum critério aplicável a esta turma simplesmente não aparece.
const ORDEM_GRUPOS = ['PARTICIPACAO', 'COMPORTAMENTO', 'FREQUENCIA', 'DESENVOLVIMENTO_TECNICO'];
const GRUPO_LABEL = {
  PARTICIPACAO: 'Participação',
  COMPORTAMENTO: 'Comportamento',
  FREQUENCIA: 'Frequência',
  DESENVOLVIMENTO_TECNICO: 'Desenvolvimento Técnico',
};

const agruparCriteriosPorGrupo = (criterios) => {
  const porGrupo = new Map();
  criterios.forEach((c) => {
    if (!porGrupo.has(c.grupo)) porGrupo.set(c.grupo, []);
    porGrupo.get(c.grupo).push(c);
  });
  return ORDEM_GRUPOS.filter((g) => porGrupo.has(g)).map((g) => ({ grupo: g, itens: porGrupo.get(g) }));
};

const normalizarComentario = (valor) => {
  if (valor == null) return null;
  const limpa = valor.trim();
  return limpa === '' ? null : limpa;
};

// ─────────────────────────────────────────────────────────────
// Seletor de nível de um critério -- 3 botões horizontais (empilham em
// telas estreitas via flex-wrap, mesma classe .portal-professor__status-*
// já usada pela chamada de frequência para o mesmo tipo de escolha
// tocável). Nunca depende só de cor: o texto do botão e o aria-pressed
// já expressam a seleção.
// ─────────────────────────────────────────────────────────────
const CriterioBloco = ({ criterio, nivelSelecionado, somenteLeitura, onSelecionar }) => {
  const grupoId = `criterio-nome-${criterio.id_periodo_criterio}`;

  return (
    <div className="portal-professor__criterio-bloco" data-testid="criterio-bloco">
      <Text strong id={grupoId}>
        {criterio.nome}
      </Text>
      {criterio.descricao && (
        <Text type="secondary" className="portal-professor__criterio-descricao">
          {criterio.descricao}
        </Text>
      )}

      {somenteLeitura ? (
        <Tag color={nivelSelecionado ? NIVEL_OPCOES.find((o) => o.value === nivelSelecionado)?.color : 'default'}>
          {nivelSelecionado ? NIVEL_LABEL[nivelSelecionado] : 'Sem resposta'}
        </Tag>
      ) : (
        <div className="portal-professor__nivel-opcoes" role="group" aria-labelledby={grupoId}>
          {NIVEL_OPCOES.map((opcao) => {
            const selecionado = nivelSelecionado === opcao.value;
            return (
              <Button
                key={opcao.value}
                className="portal-professor__nivel-btn"
                aria-pressed={selecionado}
                aria-label={`${criterio.nome}: marcar como ${opcao.label}`}
                type={selecionado ? 'primary' : 'default'}
                style={selecionado ? { backgroundColor: opcao.color, borderColor: opcao.color } : undefined}
                onClick={() => onSelecionar(criterio.id_periodo_criterio, opcao.value)}
              >
                {opcao.label}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Página
// ─────────────────────────────────────────────────────────────

const PortalProfessorAvaliacaoAluno = () => {
  const { avaliacaoAlunoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { avaliacaoTurmaId, idsAlunosNaOrdem } = location.state || {};

  const [detalhe, setDetalhe] = useState(null); // baseline do servidor
  const [comentarioLocal, setComentarioLocal] = useState('');
  const [respostasLocal, setRespostasLocal] = useState({}); // { [idPeriodoCriterio]: nivel | null }

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // { status, message } | null
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState(null);
  const [submetendo, setSubmetendo] = useState(false);
  const [erroSubmeter, setErroSubmeter] = useState(null);
  const [proximoAlunoDisponivel, setProximoAlunoDisponivel] = useState(false);

  const sincronizarComServidor = useCallback((dados) => {
    setDetalhe(dados);
    setComentarioLocal(dados.comentario || '');
    setRespostasLocal(Object.fromEntries(dados.criterios.map((c) => [c.id_periodo_criterio, c.nivel_atual])));
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    setErroSalvar(null);
    setErroSubmeter(null);
    setProximoAlunoDisponivel(false);
    try {
      const response = await professorAvaliacoesService.obterAvaliacao(avaliacaoAlunoId);
      sincronizarComServidor(response.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErro({ status, message: MENSAGEM_SEM_VINCULO });
      } else if (status === 404) {
        setErro({ status, message: MENSAGEM_NAO_ENCONTRADA });
      } else {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível carregar esta avaliação. Tente novamente.'
        );
        setErro({ status, message: mensagem });
      }
    } finally {
      setLoading(false);
    }
  }, [avaliacaoAlunoId, sincronizarComServidor]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const criterios = detalhe?.criterios || [];
  // Fase 12: `disponivel_para_escrita` já é o gate EFETIVO e completo
  // (status PENDENTE/RASCUNHO + janela de datas do período, incluindo a
  // exceção estrita de RASCUNHO devolvido para ajuste) -- nunca
  // reimplementa a regra por conta própria a partir de status_periodo.
  const somenteLeitura = detalhe ? !detalhe.disponivel_para_escrita : false;
  const aguardandoValidacao = detalhe?.status === 'AGUARDANDO_VALIDACAO';
  const publicada = detalhe?.status === 'PUBLICADA';
  // RASCUNHO/PENDENTE fora da janela de escrita (nem status nem ajuste
  // explicam o bloqueio) -- só resta a data do período já ter passado.
  const periodoFechadoPorData = somenteLeitura && !aguardandoValidacao && !publicada;
  const ajusteSolicitado = detalhe?.status === 'RASCUNHO' && !!detalhe?.motivo_ajuste;

  const houveAlteracao =
    normalizarComentario(comentarioLocal) !== normalizarComentario(detalhe?.comentario) ||
    criterios.some((c) => (respostasLocal[c.id_periodo_criterio] || null) !== (c.nivel_atual || null));

  const todosPreenchidos = criterios.length > 0 && criterios.every((c) => !!respostasLocal[c.id_periodo_criterio]);

  // Semântica de REPLACE COMPLETO (mesma do backend, ver
  // avaliacao_aluno_service.py): o array enviado representa o estado
  // INTEIRO do formulário -- um critério sem nível selecionado é OMITIDO
  // aqui de propósito, nunca enviado com nivel=null, pois o backend
  // remove qualquer resposta existente que não venha no payload. Enviar
  // só "o último critério alterado" apagaria as demais respostas já
  // salvas.
  const construirPayloadRespostas = () =>
    Object.entries(respostasLocal)
      .filter(([, nivel]) => nivel != null)
      .map(([idPeriodoCriterio, nivel]) => ({ id_periodo_criterio: Number(idPeriodoCriterio), nivel }));

  const handleSelecionarNivel = (idCriterio, nivel) => {
    setRespostasLocal((atual) => ({ ...atual, [idCriterio]: nivel }));
  };

  const salvarNoServidor = async () => {
    const payload = { comentario: normalizarComentario(comentarioLocal), respostas: construirPayloadRespostas() };
    const response = await professorAvaliacoesService.salvarRascunho(avaliacaoAlunoId, payload);
    sincronizarComServidor(response.data);
    return response.data;
  };

  const handleSalvarRascunho = async () => {
    if (salvando || somenteLeitura || !houveAlteracao) return; // evita duplo clique e envio inútil
    setSalvando(true);
    setErroSalvar(null);
    try {
      await salvarNoServidor();
      message.success('Rascunho salvo com sucesso.');
    } catch (err) {
      setErroSalvar(mapearErroEscrita(err));
    } finally {
      setSalvando(false);
    }
  };

  const submissaoComSucesso = () => {
    message.success('Avaliação enviada para validação com sucesso.');
    const indiceAtual = (idsAlunosNaOrdem || []).indexOf(Number(avaliacaoAlunoId));
    const existeProximo = indiceAtual >= 0 && indiceAtual < (idsAlunosNaOrdem || []).length - 1;
    setProximoAlunoDisponivel(existeProximo);
  };

  const executarSubmissao = async () => {
    if (submetendo) return; // evita duplo clique
    setSubmetendo(true);
    setErroSubmeter(null);
    try {
      // Garante WYSIWYG: se há alteração local ainda não salva, salva
      // primeiro (mesmo endpoint de rascunho) para que o que é enviado
      // para validação seja exatamente o que o professor vê selecionado
      // na tela -- o endpoint de submissão não recebe payload, ele envia
      // o que já está persistido.
      if (houveAlteracao) {
        await salvarNoServidor();
      }
      const response = await professorAvaliacoesService.submeterParaValidacao(avaliacaoAlunoId);
      sincronizarComServidor(response.data);
      submissaoComSucesso();
    } catch (err) {
      setErroSubmeter(mapearErroEscrita(err));
    } finally {
      setSubmetendo(false);
    }
  };

  const handleAbrirConfirmacaoSubmissao = () => {
    if (!todosPreenchidos) {
      message.warning('Preencha todos os critérios antes de enviar para validação.');
      return;
    }
    Modal.confirm({
      title: 'Enviar para validação?',
      content:
        'Deseja enviar esta avaliação para validação da gestão? Depois de enviada, você não poderá mais editá-la, a menos que a gestão solicite um ajuste.',
      okText: 'Enviar para validação',
      cancelText: 'Cancelar',
      onOk: executarSubmissao,
    });
  };

  const mapearErroEscrita = (err) => {
    const status = err?.response?.status;
    if (status === 403) return { status, message: MENSAGEM_SEM_VINCULO };
    if (status === 404) return { status, message: MENSAGEM_NAO_ENCONTRADA };
    if (status === 409) {
      return {
        status,
        message: 'Esta avaliação não pode mais ser editada agora. Atualize a página para ver o estado atual.',
        podeAtualizar: true,
      };
    }
    if (status === 422) {
      return {
        status,
        message: 'Não foi possível concluir: verifique se todos os critérios estão preenchidos corretamente.',
        podeAtualizar: true,
      };
    }
    const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível concluir a operação. Tente novamente.');
    return { status, message: mensagem };
  };

  const voltar = () => {
    if (avaliacaoTurmaId) {
      navigate(`/professor/avaliacoes/turmas/${avaliacaoTurmaId}`);
    } else {
      navigate(-1);
    }
  };

  const irParaProximoAluno = () => {
    const indiceAtual = (idsAlunosNaOrdem || []).indexOf(Number(avaliacaoAlunoId));
    const proximoId = idsAlunosNaOrdem?.[indiceAtual + 1];
    if (!proximoId) return;
    navigate(`/professor/avaliacoes/alunos/${proximoId}`, {
      state: { avaliacaoTurmaId, idsAlunosNaOrdem },
    });
  };

  const statusInfo = detalhe
    ? STATUS_ALUNO_AVALIACAO_INFO[detalhe.status] || { label: detalhe.status, color: 'default' }
    : null;

  return (
    <div className="portal-professor">
      <Button icon={<ArrowLeftOutlined />} type="text" onClick={voltar} aria-label="Voltar" style={{ marginBottom: 12 }}>
        Voltar
      </Button>

      {loading && (
        <div className="portal-professor__state" role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">Carregando avaliação...</Text>
        </div>
      )}

      {!loading && erro && (
        <div className="portal-professor__state">
          <Alert
            type={erro.status === 403 || erro.status === 404 ? 'warning' : 'error'}
            showIcon
            message="Não foi possível abrir a avaliação"
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 403 && erro.status !== 404 && (
            <Button icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          )}
        </div>
      )}

      {!loading && !erro && detalhe && (
        <div className="portal-professor__list">
          <div className="portal-professor__chamada-header">
            <Title level={4} style={{ marginBottom: 4 }}>
              {detalhe.nome_aluno}
            </Title>
            <Text type="secondary">
              {detalhe.nome_turma} • {detalhe.nome_modalidade}
            </Text>
            <br />
            <Text type="secondary">{detalhe.nome_periodo}</Text>
            <div style={{ marginTop: 8 }}>
              <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
            </div>
          </div>

          {aguardandoValidacao && (
            <Alert
              type="info"
              showIcon
              message="Avaliação enviada e aguardando validação."
              description="A gestão vai revisar esta avaliação. Você poderá editá-la novamente se um ajuste for solicitado."
            />
          )}

          {publicada && (
            <Alert
              type="success"
              showIcon
              message="Avaliação validada e publicada."
              description="Esta avaliação já está disponível para o responsável e não pode mais ser editada."
            />
          )}

          {periodoFechadoPorData && (
            <Alert
              type="default"
              showIcon
              message="Período encerrado"
              description="Período encerrado — avaliação disponível somente para consulta."
            />
          )}

          {ajusteSolicitado && (
            <Alert
              type="warning"
              showIcon
              message="Ajuste solicitado"
              description={detalhe.motivo_ajuste}
            />
          )}

          {erroSalvar && (
            <Alert
              type={erroSalvar.status === 403 || erroSalvar.status === 404 ? 'warning' : 'error'}
              showIcon
              message="Não foi possível salvar"
              description={erroSalvar.message}
              action={
                erroSalvar.podeAtualizar ? (
                  <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
                    Atualizar
                  </Button>
                ) : undefined
              }
            />
          )}

          {erroSubmeter && (
            <Alert
              type={erroSubmeter.status === 403 || erroSubmeter.status === 404 ? 'warning' : 'error'}
              showIcon
              message="Não foi possível enviar para validação"
              description={erroSubmeter.message}
              action={
                erroSubmeter.podeAtualizar ? (
                  <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
                    Atualizar
                  </Button>
                ) : undefined
              }
            />
          )}

          {proximoAlunoDisponivel && (
            <Alert
              type="success"
              showIcon
              message="Avaliação enviada para validação"
              description="Deseja continuar para o próximo aluno?"
              action={
                <Button size="small" type="primary" onClick={irParaProximoAluno}>
                  Próximo aluno
                </Button>
              }
            />
          )}

          <div className="portal-professor__alunos-lista">
            {agruparCriteriosPorGrupo(criterios).map(({ grupo, itens }) => (
              <React.Fragment key={grupo}>
                <Text strong type="secondary" className="portal-professor__criterio-grupo-titulo">
                  {GRUPO_LABEL[grupo] || grupo}
                </Text>
                {itens.map((criterio) => (
                  <CriterioBloco
                    key={criterio.id_periodo_criterio}
                    criterio={criterio}
                    nivelSelecionado={respostasLocal[criterio.id_periodo_criterio]}
                    somenteLeitura={somenteLeitura}
                    onSelecionar={handleSelecionarNivel}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>

          <div className="portal-professor__criterio-bloco">
            <Text strong id="comentario-label">
              Observações sobre a evolução do aluno
            </Text>
            <TextArea
              aria-labelledby="comentario-label"
              value={comentarioLocal}
              onChange={(e) => setComentarioLocal(e.target.value)}
              placeholder="Observação (opcional)"
              autoSize={{ minRows: 2, maxRows: 6 }}
              maxLength={2000}
              disabled={somenteLeitura}
            />
          </div>

          {!somenteLeitura && (
            <>
              <Button
                size="large"
                block
                loading={salvando}
                disabled={salvando || !houveAlteracao}
                onClick={handleSalvarRascunho}
                style={{ minHeight: 48 }}
              >
                Salvar rascunho
              </Button>

              <Button
                type="primary"
                size="large"
                block
                loading={submetendo}
                disabled={submetendo}
                onClick={handleAbrirConfirmacaoSubmissao}
                style={{ minHeight: 48 }}
              >
                Enviar para validação
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalProfessorAvaliacaoAluno;
